import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import PageBackButton from '../../components/PageBackButton';
import { Select, SelectTrigger, SelectContent, SelectItem } from '../../components/ui/select';
import { runScreener, searchSymbols } from '../../api/dataWarehouseApi';
import {
  Search, Plus, X, ChevronUp, ChevronDown, Loader2, Filter,
  ArrowUpDown, RotateCcw, Bookmark, TrendingUp, TrendingDown,
  BarChart3, Zap, Activity, Target, ChevronLeft, ChevronRight,
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────

function fmt(val, decimals = 2) {
  if (val == null) return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtBig(val) {
  if (val == null) return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  if (Math.abs(n) >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString();
}

function fmtPct(val) {
  if (val == null) return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
}

function colorForChange(val) {
  if (val == null) return '';
  return Number(val) >= 0 ? 'text-success' : 'text-destructive';
}

// ── Operator labels ──────────────────────────────────────────────
const OPS = [
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'eq', label: '=' },
  { value: 'ne', label: '!=' },
];

// ── Field categories for the filter picker ───────────────────────
const FIELD_CATEGORIES = {
  'Price': ['close', 'open', 'high', 'low', 'change_pct', 'change_abs', 'gap_pct'],
  'Trend': ['sma_20', 'sma_50', 'sma_100', 'sma_200', 'ema_20', 'ema_50', 'price_vs_sma20', 'price_vs_sma50', 'price_vs_sma200'],
  'Momentum': ['rsi_14', 'macd_line', 'macd_signal', 'macd_histogram', 'stoch_k', 'stoch_d', 'adx_14', 'plus_di', 'minus_di', 'cci_14', 'williams_r'],
  'Volatility': ['atr_14', 'bb_upper', 'bb_middle', 'bb_lower', 'bb_width_pct'],
  'Volume': ['volume', 'obv', 'vwap', 'cmf_20', 'mfi_14', 'volume_sma_20', 'volume_ratio'],
  'Price Action': ['high_52w', 'low_52w', 'dist_from_52w_high', 'dist_from_52w_low'],
  'Fundamental': ['market_cap', 'pe_ratio', 'eps', 'dividend_yield', 'revenue', 'profit_margin', 'beta'],
};

const ALL_FILTERABLE_FIELDS = Object.values(FIELD_CATEGORIES).flat();

function fieldLabel(name) {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ── Preset filters ───────────────────────────────────────────────
const PRESETS = [
  { label: 'Oversold (RSI < 30)', icon: TrendingDown, filters: [{ field: 'rsi_14', op: 'lt', value: 30 }] },
  { label: 'Overbought (RSI > 70)', icon: TrendingUp, filters: [{ field: 'rsi_14', op: 'gt', value: 70 }] },
  { label: 'Strong Trend (ADX > 25)', icon: Zap, filters: [{ field: 'adx_14', op: 'gt', value: 25 }] },
  { label: 'High Volume', icon: BarChart3, filters: [{ field: 'volume_ratio', op: 'gt', value: 2 }] },
  { label: 'Near 52W High', icon: Target, filters: [{ field: 'dist_from_52w_high', op: 'gt', value: -5 }] },
  { label: 'Golden Cross Possible', icon: Activity, filters: [{ field: 'price_vs_sma50', op: 'gt', value: 0 }, { field: 'price_vs_sma200', op: 'gt', value: 0 }] },
  { label: 'Large Cap (>$10B)', icon: TrendingUp, filters: [{ field: 'market_cap', op: 'gt', value: 10000000000 }] },
  { label: 'High Dividend (>3%)', icon: TrendingUp, filters: [{ field: 'dividend_yield', op: 'gt', value: 3 }] },
];

// ── Asset class tabs ─────────────────────────────────────────────
const ASSET_CLASSES = [
  { value: '', label: 'All' },
  { value: 'STOCK', label: 'Stocks' },
  { value: 'FOREX', label: 'Forex' },
  { value: 'CRYPTO', label: 'Crypto' },
  { value: 'ETF', label: 'ETFs' },
  { value: 'COMMODITY', label: 'Commodities' },
  { value: 'INDEX', label: 'Indices' },
];

// ── Default visible columns ──────────────────────────────────────
const DEFAULT_COLUMNS = [
  'symbol', 'name', 'close', 'change_pct', 'volume', 'rsi_14', 'adx_14',
  'macd_histogram', 'sma_50', 'market_cap',
];

const COLUMN_DEFS = {
  symbol: { label: 'Symbol', align: 'left', width: 'w-24' },
  name: { label: 'Name', align: 'left', width: 'min-w-[140px] max-w-[200px]' },
  close: { label: 'Price', align: 'right', format: v => fmt(v), mono: true },
  open: { label: 'Open', align: 'right', format: v => fmt(v), mono: true },
  high: { label: 'High', align: 'right', format: v => fmt(v), mono: true },
  low: { label: 'Low', align: 'right', format: v => fmt(v), mono: true },
  change_pct: { label: 'Change %', align: 'right', format: v => fmtPct(v), color: true },
  change_abs: { label: 'Change', align: 'right', format: v => fmt(v, 4), color: true },
  volume: { label: 'Volume', align: 'right', format: v => fmtBig(v) },
  rsi_14: { label: 'RSI', align: 'right', format: v => fmt(v, 1) },
  adx_14: { label: 'ADX', align: 'right', format: v => fmt(v, 1) },
  macd_histogram: { label: 'MACD Hist', align: 'right', format: v => fmt(v, 4), color: true },
  macd_line: { label: 'MACD', align: 'right', format: v => fmt(v, 4) },
  macd_signal: { label: 'Signal', align: 'right', format: v => fmt(v, 4) },
  sma_20: { label: 'SMA 20', align: 'right', format: v => fmt(v) },
  sma_50: { label: 'SMA 50', align: 'right', format: v => fmt(v) },
  sma_100: { label: 'SMA 100', align: 'right', format: v => fmt(v) },
  sma_200: { label: 'SMA 200', align: 'right', format: v => fmt(v) },
  ema_20: { label: 'EMA 20', align: 'right', format: v => fmt(v) },
  ema_50: { label: 'EMA 50', align: 'right', format: v => fmt(v) },
  stoch_k: { label: 'Stoch %K', align: 'right', format: v => fmt(v, 1) },
  stoch_d: { label: 'Stoch %D', align: 'right', format: v => fmt(v, 1) },
  cci_14: { label: 'CCI', align: 'right', format: v => fmt(v, 1) },
  williams_r: { label: 'Williams %R', align: 'right', format: v => fmt(v, 1) },
  atr_14: { label: 'ATR', align: 'right', format: v => fmt(v, 4) },
  bb_upper: { label: 'BB Upper', align: 'right', format: v => fmt(v) },
  bb_lower: { label: 'BB Lower', align: 'right', format: v => fmt(v) },
  bb_width_pct: { label: 'BB Width %', align: 'right', format: v => fmt(v, 2) },
  obv: { label: 'OBV', align: 'right', format: v => fmtBig(v) },
  vwap: { label: 'VWAP', align: 'right', format: v => fmt(v) },
  cmf_20: { label: 'CMF', align: 'right', format: v => fmt(v, 4) },
  mfi_14: { label: 'MFI', align: 'right', format: v => fmt(v, 1) },
  volume_ratio: { label: 'Vol Ratio', align: 'right', format: v => fmt(v, 2) },
  price_vs_sma20: { label: 'vs SMA20 %', align: 'right', format: v => fmtPct(v), color: true },
  price_vs_sma50: { label: 'vs SMA50 %', align: 'right', format: v => fmtPct(v), color: true },
  price_vs_sma200: { label: 'vs SMA200 %', align: 'right', format: v => fmtPct(v), color: true },
  high_52w: { label: '52W High', align: 'right', format: v => fmt(v) },
  low_52w: { label: '52W Low', align: 'right', format: v => fmt(v) },
  dist_from_52w_high: { label: '% from 52H', align: 'right', format: v => fmtPct(v), color: true },
  dist_from_52w_low: { label: '% from 52L', align: 'right', format: v => fmtPct(v), color: true },
  gap_pct: { label: 'Gap %', align: 'right', format: v => fmtPct(v), color: true },
  market_cap: { label: 'Mkt Cap', align: 'right', format: v => fmtBig(v) },
  pe_ratio: { label: 'P/E', align: 'right', format: v => fmt(v, 1) },
  eps: { label: 'EPS', align: 'right', format: v => fmt(v, 2) },
  dividend_yield: { label: 'Div Yield %', align: 'right', format: v => fmt(v, 2) },
  revenue: { label: 'Revenue', align: 'right', format: v => fmtBig(v) },
  profit_margin: { label: 'Profit Margin', align: 'right', format: v => fmt(v, 2) },
  beta: { label: 'Beta', align: 'right', format: v => fmt(v, 2) },
};

// ══════════════════════════════════════════════════════════════════
// ScreenerPage — Main Component
// ══════════════════════════════════════════════════════════════════

export default function ScreenerPage() {
  const navigate = useNavigate();

  // ── State ──
  const [assetClass, setAssetClass] = useState('STOCK');
  const [filters, setFilters] = useState([]);
  const [sort, setSort] = useState({ field: 'symbol', direction: 'asc' });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showFilterBuilder, setShowFilterBuilder] = useState(false);
  const [quickSearch, setQuickSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // New filter row state
  const [newField, setNewField] = useState('rsi_14');
  const [newOp, setNewOp] = useState('lt');
  const [newValue, setNewValue] = useState('');

  const totalPages = Math.ceil(totalCount / pageSize);

  // ── Run screener ──
  const fetchScreener = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError(null);
    try {
      const body = {
        timeframe: 'D1',
        filters: overrides.filters ?? filters,
        asset_class: overrides.asset_class ?? assetClass,
        sort: overrides.sort ?? sort,
        page: overrides.page ?? page,
        page_size: pageSize,
      };
      const res = await runScreener(body);
      setResults(res?.results || []);
      setTotalCount(res?.total || 0);
    } catch (err) {
      setError(err?.message || 'Failed to load screener data');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [filters, assetClass, sort, page, pageSize]);

  // Initial load + reload on changes
  useEffect(() => {
    fetchScreener();
  }, [fetchScreener]);

  // ── Quick search ──
  useEffect(() => {
    const q = quickSearch.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    setSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await searchSymbols(q);
        setSearchResults(res?.results || []);
      } catch { setSearchResults([]); }
      finally { setSearchLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [quickSearch]);

  // ── Filter actions ──
  const addFilter = () => {
    const val = parseFloat(newValue);
    if (Number.isNaN(val)) return;
    setFilters(prev => [...prev, { field: newField, op: newOp, value: val }]);
    setNewValue('');
    setPage(1);
  };

  const removeFilter = (idx) => {
    setFilters(prev => prev.filter((_, i) => i !== idx));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters([]);
    setPage(1);
  };

  const applyPreset = (preset) => {
    setFilters(preset.filters);
    setPage(1);
  };

  // ── Sort toggle ──
  const toggleSort = (field) => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setPage(1);
  };

  // ── Column toggle ──
  const toggleColumn = (col) => {
    setColumns(prev => prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]);
  };

  return (
    <div className="my-2 sm:my-4 md:my-6 mx-auto max-w-[100rem] space-y-3">
      {/* ── Header ── */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <PageBackButton label="Back to Research" to="/research" preferHistory={false} hideLabelOnMobile />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-primary">Market Screener</h1>
                <p className="text-sm text-textSecondary mt-0.5">
                  Filter {totalCount.toLocaleString()} instruments across technical and fundamental criteria
                </p>
              </div>
            </div>

            {/* Quick symbol search */}
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textSecondary pointer-events-none z-10" />
              <input
                type="text"
                placeholder="Search symbol or name..."
                value={quickSearch}
                onChange={e => setQuickSearch(e.target.value)}
                className="w-full rounded-md border border-borderDefault bg-backgroundInput text-textPrimary
                  placeholder-textSecondary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                  pl-9 pr-4 py-2 text-sm"
              />
              {(searchResults.length > 0 || searchLoading) && quickSearch.length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-backgroundSurface border border-borderDefault rounded-lg shadow-xl max-h-64 overflow-auto">
                  {searchLoading && <div className="p-3 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>}
                  {searchResults.map(s => (
                    <button key={s.symbol}
                      onClick={() => { navigate(`/explorer/${s.symbol}`); setQuickSearch(''); setSearchResults([]); }}
                      className="w-full px-3 py-2 text-left hover:bg-primary/10 transition flex items-center gap-2 text-sm">
                      <span className="font-mono font-semibold text-primary">{s.symbol}</span>
                      <span className="text-textSecondary truncate">{s.name}</span>
                      {s.asset_class && <Badge className="ml-auto flex-shrink-0">{s.asset_class}</Badge>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Asset Class Tabs ── */}
      <div className="flex gap-1 flex-wrap bg-backgroundSurface rounded-xl border border-borderDefault p-1">
        {ASSET_CLASSES.map(ac => (
          <button key={ac.value}
            onClick={() => { setAssetClass(ac.value); setPage(1); }}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition whitespace-nowrap ${
              assetClass === ac.value ? 'bg-primary text-textAccent' : 'text-textSecondary hover:bg-primary/10'
            }`}
          >
            {ac.label}
          </button>
        ))}
      </div>

      {/* ── Presets ── */}
      <div className="flex gap-2 flex-wrap">
        {PRESETS.map((p, i) => (
          <button key={i} onClick={() => applyPreset(p)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-borderDefault bg-backgroundSurface hover:bg-primary/10 hover:border-primary/30 transition">
            <p.icon className="w-3.5 h-3.5 text-primary" />
            {p.label}
          </button>
        ))}
      </div>

      {/* ── Filter Builder ── */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <button onClick={() => setShowFilterBuilder(!showFilterBuilder)}
              className="flex items-center gap-2 text-sm font-medium text-textPrimary hover:text-primary transition">
              <Filter className="w-4 h-4" />
              Filters ({filters.length})
              {showFilterBuilder ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {filters.length > 0 && (
              <button onClick={clearFilters} className="text-xs text-textSecondary hover:text-destructive transition flex items-center gap-1">
                <RotateCcw className="w-3 h-3" /> Clear All
              </button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <button onClick={() => setShowColumnPicker(!showColumnPicker)}
                className="text-sm text-textSecondary hover:text-primary transition flex items-center gap-1">
                <ArrowUpDown className="w-4 h-4" /> Columns
              </button>
            </div>
          </div>

          {/* Active filters */}
          {filters.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {filters.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-primary/10 text-primary rounded-full border border-primary/20">
                  <span className="font-medium">{fieldLabel(f.field)}</span>
                  <span>{OPS.find(o => o.value === f.op)?.label || f.op}</span>
                  <span className="font-mono">{typeof f.value === 'number' && Math.abs(f.value) > 1e6 ? fmtBig(f.value) : f.value}</span>
                  <button onClick={() => removeFilter(i)} className="hover:text-destructive ml-0.5"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}

          {/* Add filter row */}
          {showFilterBuilder && (
            <div className="flex flex-wrap items-end gap-2 p-3 bg-backgroundInput rounded-lg">
              <div className="flex-1 min-w-[160px]">
                <label className="text-xs text-textSecondary mb-1 block">Field</label>
                <select value={newField} onChange={e => setNewField(e.target.value)}
                  className="w-full rounded-md border border-borderDefault bg-backgroundSurface text-textPrimary px-2 py-1.5 text-sm">
                  {Object.entries(FIELD_CATEGORIES).map(([cat, fields]) => (
                    <optgroup key={cat} label={cat}>
                      {fields.map(f => <option key={f} value={f}>{fieldLabel(f)}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div className="w-24">
                <label className="text-xs text-textSecondary mb-1 block">Operator</label>
                <select value={newOp} onChange={e => setNewOp(e.target.value)}
                  className="w-full rounded-md border border-borderDefault bg-backgroundSurface text-textPrimary px-2 py-1.5 text-sm">
                  {OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="w-32">
                <label className="text-xs text-textSecondary mb-1 block">Value</label>
                <Input
                  type="number"
                  value={newValue}
                  onChange={e => setNewValue(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addFilter()}
                  placeholder="0"
                  className="text-sm"
                />
              </div>
              <Button size="sm" onClick={addFilter} className="flex-shrink-0">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          )}

          {/* Column picker */}
          {showColumnPicker && (
            <div className="mt-3 p-3 bg-backgroundInput rounded-lg">
              <p className="text-xs text-textSecondary mb-2 font-medium">Visible Columns</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(COLUMN_DEFS).filter(([key]) => key !== 'symbol').map(([key, def]) => (
                  <button key={key}
                    onClick={() => toggleColumn(key)}
                    className={`px-2 py-1 text-xs rounded-md border transition ${
                      columns.includes(key)
                        ? 'bg-primary/10 border-primary/30 text-primary'
                        : 'border-borderDefault text-textSecondary hover:border-primary/20'
                    }`}
                  >
                    {def.label}
                  </button>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <button onClick={() => setColumns(DEFAULT_COLUMNS)} className="text-xs text-primary hover:underline">Reset</button>
                <button onClick={() => setColumns(Object.keys(COLUMN_DEFS))} className="text-xs text-primary hover:underline">Show All</button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Results Table ── */}
      <Card>
        <CardContent className="p-0">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
              <span className="text-textSecondary">Loading...</span>
            </div>
          )}

          {error && !loading && (
            <div className="text-center py-12">
              <p className="text-destructive mb-2">{error}</p>
              <Button variant="outline" size="sm" onClick={() => fetchScreener()}>Retry</Button>
            </div>
          )}

          {!loading && !error && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-borderDefault bg-backgroundInput/50">
                    {columns.filter(c => COLUMN_DEFS[c]).map(col => {
                      const def = COLUMN_DEFS[col];
                      const isSorted = sort.field === col;
                      const sortable = col !== 'name'; // name comes from join, not sortable directly
                      return (
                        <th key={col}
                          onClick={() => sortable && toggleSort(col)}
                          className={`py-2.5 px-3 text-xs font-semibold text-textSecondary whitespace-nowrap ${def.align === 'right' ? 'text-right' : 'text-left'} ${def.width || ''} ${sortable ? 'cursor-pointer hover:text-primary select-none' : ''}`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {def.label}
                            {isSorted && (sort.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {results.length === 0 && (
                    <tr><td colSpan={columns.length} className="text-center py-12 text-textSecondary">No results. Adjust your filters or asset class.</td></tr>
                  )}
                  {results.map((row, i) => (
                    <tr key={row.symbol || i}
                      onClick={() => navigate(`/explorer/${row.symbol}`)}
                      className="border-b border-borderDefault/50 hover:bg-primary/5 transition cursor-pointer">
                      {columns.filter(c => COLUMN_DEFS[c]).map(col => {
                        const def = COLUMN_DEFS[col];
                        const val = row[col];

                        // Special rendering for symbol
                        if (col === 'symbol') {
                          return (
                            <td key={col} className="py-2.5 px-3">
                              <span className="font-mono font-semibold text-primary">{val}</span>
                            </td>
                          );
                        }
                        if (col === 'name') {
                          return (
                            <td key={col} className={`py-2.5 px-3 text-textSecondary truncate ${def.width}`}>{val || '—'}</td>
                          );
                        }

                        const formatted = def.format ? def.format(val) : (val ?? '—');
                        const colorCls = def.color ? colorForChange(val) : '';

                        return (
                          <td key={col} className={`py-2.5 px-3 whitespace-nowrap ${def.align === 'right' ? 'text-right' : 'text-left'} ${def.mono ? 'font-mono' : ''} ${colorCls}`}>
                            {formatted}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && totalCount > 0 && (
            <div className="flex items-center justify-between p-3 border-t border-borderDefault">
              <p className="text-sm text-textSecondary">
                Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount.toLocaleString()}
              </p>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let p;
                  if (totalPages <= 5) p = i + 1;
                  else if (page <= 3) p = i + 1;
                  else if (page >= totalPages - 2) p = totalPages - 4 + i;
                  else p = page - 2 + i;
                  return (
                    <button key={p} onClick={() => setPage(p)}
                      className={`w-8 h-8 text-sm rounded-md transition ${page === p ? 'bg-primary text-textAccent' : 'text-textSecondary hover:bg-primary/10'}`}>
                      {p}
                    </button>
                  );
                })}
                <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
